import React from 'react';

interface StepIndicatorProps {
  steps: Array<{
    id: number;
    label: string;
    completed?: boolean;
  }>;
  currentStep: number;
}

const CheckIcon: React.FC = () => (
  <svg
    className="w-4 h-4 text-static-white"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    strokeWidth={2.5}
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const StepIndicator: React.FC<StepIndicatorProps> = ({ steps, currentStep }) => {
  const getStepStatus = (stepId: number) => {
    if (currentStep > stepId) return 'completed';
    if (currentStep === stepId) return 'current';
    return 'upcoming';
  };

  const getStepStyles = (status: string) => {
    switch (status) {
      case 'completed':
        return {
          circle: 'bg-success-base text-static-white border-success-base',
          text: 'text-success-dark font-medium',
        };
      case 'current':
        return {
          circle: 'bg-primary text-static-white border-primary',
          text: 'text-primary font-medium',
        };
      case 'upcoming':
      default:
        return {
          circle: 'bg-bg-weak-50 text-text-soft-400 border-stroke-sub-300',
          text: 'text-text-soft-400',
        };
    }
  };

  return (
    <div className="flex items-center">
      {steps.map((step, index) => {
        const status = getStepStatus(step.id);
        const styles = getStepStyles(status);
        const isCompleted = status === 'completed';

        return (
          <React.Fragment key={step.id}>
            <div className="flex items-center">
              <div
                className={`
                  w-6 h-6 rounded-full border-2 flex items-center justify-center text-xs font-semibold
                  transition-all duration-200 ease-in-out
                  ${styles.circle}
                `}
              >
                {isCompleted ? <CheckIcon /> : step.id}
              </div>
              <span className={`ml-2 text-sm transition-colors ${styles.text}`}>
                {step.label}
              </span>
            </div>

            {index < steps.length - 1 && (
              <div className="mx-3 text-text-soft-400">
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  strokeWidth={2}
                >
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </div>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default StepIndicator;
